import * as storage from '../../../utils/storage';
import { IRootStore, RootStoreModel } from './RootStore';
import { captureException } from '../../../utils/sentry/sentry';
import { onSnapshot } from 'mobx-state-tree';

/**
 * The key we'll be saving our state as within async storage.
 */
const ROOT_STATE_STORAGE_KEY = 'root_clear';
const ROOT_STATE_STORAGE_KEY_ENCRYPTED = 'root_encrypted';
/**
 * The persisted CONTENT caches (sessions and their steps, trees, activities,
 * the tree navigation) can reference server content that no longer exists —
 * e.g. steps rewired to new activities by a content migration. Bump this
 * version to drop them once on the next start: they are refetched on demand.
 */
// 2026-10-28: drop the trees persisted with two nodes of the same key
const CONTENT_CACHE_VERSION = '2026-10-28-duplicate-node-keys';
const CONTENT_CACHE_VERSION_KEY = 'root_content_cache_version';
// 'challenge' is the retired store: RootStoreModel no longer declares it, so a
// snapshot written by an older build must not carry it into create()
const CONTENT_CACHE_STORES = ['session', 'trees', 'treeNav', 'activity', 'question', 'challenge'];
const ENCRYPTED_STORES_FILTER = ({ user }: any) => ({ user });
const CLEAR_STORES_FILTER = ({
  activity,
  categories,
  content,
  exercise,
  feature,
  language,
  onboarding,
  path,
  psychologist,
  question,
  reckoning,
  responses,
  session,
  survey,
  technique,
  treeNav,
  trees,
}: any) => ({
  activity,
  categories,
  content,
  exercise,
  feature,
  language,
  onboarding,
  path,
  psychologist,
  question,
  reckoning,
  responses,
  session,
  survey,
  technique,
  treeNav,
  trees,
});

/**
 * Setup the root state.
 */
export const setupRootStore = async () => {
  let rootStore: IRootStore;
  let data: any;

  try {
    // load data from storage
    const dataClear = (await storage.load(ROOT_STATE_STORAGE_KEY)) || {};
    const contentCacheVersion = await storage.loadString(CONTENT_CACHE_VERSION_KEY);
    if (contentCacheVersion !== CONTENT_CACHE_VERSION) {
      CONTENT_CACHE_STORES.forEach(store => delete dataClear[store]);
      await storage.saveString(CONTENT_CACHE_VERSION_KEY, CONTENT_CACHE_VERSION);
    }
    const dataEncrypted =
      (await storage.load(ROOT_STATE_STORAGE_KEY_ENCRYPTED, true)) || {};
    data = { ...dataClear, ...dataEncrypted };
    rootStore = RootStoreModel.create(data);
  } catch (e) {
    captureException(e);
    rootStore = RootStoreModel.create({});
  }

  // track changes & save to storage
  onSnapshot(rootStore, snapshot => {
    storage.save(ROOT_STATE_STORAGE_KEY, CLEAR_STORES_FILTER(snapshot));
    storage.save(
      ROOT_STATE_STORAGE_KEY_ENCRYPTED,
      ENCRYPTED_STORES_FILTER(snapshot),
      true,
    );
  });

  return rootStore;
};
